import React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual depth mode. Default: "flat". */
  depth?: "flat" | "bordered" | "mediaLift" | "darkProductField";
  /**
   * Tone for darkProductField depth.
   * Only applies when depth === "darkProductField".
   * Default: "deepGreen".
   */
  tone?: "deepGreen" | "darkNavy";
  /** Padding level. No padding applied when omitted. */
  padding?: "xl" | "xxl" | "section";
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Token maps — CSS custom-property references only (no hardcoded values)
// ---------------------------------------------------------------------------

const DEPTH_STYLES: Record<
  NonNullable<CardProps["depth"]>,
  (tone?: CardProps["tone"]) => React.CSSProperties
> = {
  flat: () => ({
    background: "var(--bg1)",
    border: "1px solid var(--color-card-border)",
    borderRadius: "var(--radius-md)",
  }),
  bordered: () => ({
    background: "var(--bg1)",
    border: "var(--border-hairline)",
    borderRadius: "var(--radius-sm)",
  }),
  mediaLift: () => ({
    background: "var(--bg1)",
    border: "none",
    borderRadius: "var(--radius-lg)",
  }),
  darkProductField: (tone = "deepGreen") => ({
    background:
      tone === "darkNavy" ? "var(--bg-navy)" : "var(--bg-deep-green)",
    border: "none",
    borderRadius: "var(--radius-lg)",
    color: "var(--fg-on-dark)",
  }),
};

const PADDING_MAP: Record<NonNullable<CardProps["padding"]>, string> = {
  xl: "var(--space-xl)",
  xxl: "var(--space-2xl)",
  section: "var(--space-section)",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Card({
  depth = "flat",
  tone,
  padding,
  children,
  style,
  ...rest
}: CardProps) {
  // tone only applies to darkProductField — ignored for other depths
  const depthStyle = DEPTH_STYLES[depth](
    depth === "darkProductField" ? tone : undefined,
  );

  const paddingStyle: React.CSSProperties = padding
    ? { padding: PADDING_MAP[padding] }
    : {};

  const merged: React.CSSProperties = {
    ...depthStyle,
    ...paddingStyle,
    ...style,
  };

  return (
    <div style={merged} {...rest}>
      {children}
    </div>
  );
}
